"use client";

import Reveal from "@/app/components/Reveal";
import Link from "next/link";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";
import { MailIcon, PhoneIcon, TelegramIcon, WhatsAppIcon, ContactsIcon, ChatIcon } from "./ContactIcons";
import { CONTACTS } from "@/lib/site-contacts";
import { IntegrationCard, BenefitCard } from "./IntegrationCards";

type SeoCard = { abbr: string; title: string; desc: string };

const seoCopy: Record<
    string,
    {
        title: string;
        intro: string;
        cards: SeoCard[];
        benefits: { number: string; title: string; text: string }[];
        contacts: { label: string; chatLabel: string };
    }
> = {
    ru: {
        title: "Разработка ПО для бизнеса",
        intro:
            "Dev48 разрабатывает программные решения для бизнеса — от отдельных сервисов и их интеграций до сложных информационных систем. Мы берём на себя полный цикл работ: от аудита и проектирования архитектуры до UX/UI, разработки, внедрения и интеграции с существующей IT-инфраструктурой, а после запуска обеспечиваем поддержку и развитие продукта.\n\nРаботаем с большими объёмами данных, создаём решения для их сбора, обработки, анализа и визуализации, а также внедряем ИИ в бизнес-процессы. Разрабатываем AI/ML-решения и интеллектуальные инструменты, которые помогают автоматизировать работу с данными, повышать эффективность и принимать более обоснованные решения.",
        cards: [
            { abbr: "WEB", title: "Веб-разработка", desc: "Корпоративные платформы, личные кабинеты, интернет-магазины, SaaS и порталы с интеграциями." },
            { abbr: "MOB", title: "Мобильные приложения", desc: "Нативные и кроссплатформенные приложения для iOS и Android: камеры, пуш-уведомления, платежи, офлайн-режим." },
            { abbr: "DSK", title: "Десктопные приложения", desc: "Кроссплатформенные решения для Windows, macOS и Linux с автообновлением и работой офлайн." },
            { abbr: "AI/ML", title: "Искусственный интеллект и ML", desc: "Встраиваем модели в продукт: предиктивная аналитика, генеративные функции, NLP, компьютерное зрение." },
            { abbr: "WEB3", title: "Web3 и блокчейн", desc: "Смарт-контракты, децентрализованные приложения, интеграция с крипто- и NFT-инфраструктурой." },
            { abbr: "BD", title: "Big Data и аналитика", desc: "Пайплайны данных, хранилища, ETL, BI-дашборды и поиск закономерностей в больших объёмах." },
            { abbr: "IOT", title: "IoT-решения", desc: "Подключаем датчики и устройства к облаку: телеметрия, мониторинг оборудования, edge-вычисления." },
            { abbr: "DB", title: "Базы данных", desc: "Проектирование схем, миграции, оптимизация запросов, резервирование и надёжное хранение." },
        ],
        benefits: [
            { number: "01", title: "Полный цикл работ", text: "От аудита и проектирования архитектуры до UX/UI, разработки, внедрения и поддержки продукта после запуска." },
            { number: "02", title: "Прозрачные сроки и бюджет", text: "Работаем спринтами по две недели и показываем демо после каждого. Объём и стоимость зафиксированы в договоре — без скрытых доплат." },
            { number: "03", title: "Ваш стек, а не наш", text: "React, Next.js, Node.js, Python, Swift, Kotlin, Java, PHP, 1С. Интегрируемся в вашу инфраструктуру, а не заставляем мигрировать." },
            { number: "04", title: "Надёжность и безопасность", text: "Автотесты, code review, мониторинг и безопасная разработка. Работаем on-premise, соблюдаем 152-ФЗ и GDPR." },
        ],
        contacts: { label: "Контакты", chatLabel: "Обсудить в чате" },
    },
    en: {
        title: "Custom Software Development for Business",
        intro:
            "Dev48 develops software solutions for business — from individual services and their integrations to complex information systems. We take on the full cycle of work: from audit and architecture design to UX/UI, development, implementation, and integration with the existing IT infrastructure, and after launch we provide support and product development.\n\nWe work with large volumes of data, create solutions for their collection, processing, analysis, and visualization, and also implement AI into business processes. We develop AI/ML solutions and intelligent tools that help automate work with data, increase efficiency, and make more informed decisions.",
        cards: [
            { abbr: "WEB", title: "Web Development", desc: "Corporate platforms, customer portals, e-commerce, SaaS, and portals with integrations." },
            { abbr: "MOB", title: "Mobile Apps", desc: "Native and cross-platform apps for iOS and Android: cameras, push, payments, offline mode." },
            { abbr: "DSK", title: "Desktop Apps", desc: "Cross-platform solutions for Windows, macOS, and Linux with auto-update and offline work." },
            { abbr: "AI/ML", title: "Artificial Intelligence & ML", desc: "We embed models into your product: predictive analytics, generative features, NLP, computer vision." },
            { abbr: "WEB3", title: "Web3 & Blockchain", desc: "Smart contracts, decentralized apps, and integration with crypto and NFT infrastructure." },
            { abbr: "BD", title: "Big Data & Analytics", desc: "Data pipelines, warehouses, ETL, BI dashboards, and pattern discovery at scale." },
            { abbr: "IOT", title: "IoT Solutions", desc: "We connect sensors and devices to the cloud: telemetry, equipment monitoring, edge computing." },
            { abbr: "DB", title: "Databases", desc: "Schema design, migrations, query optimization, replication, and reliable storage." },
        ],
        benefits: [
            { number: "01", title: "Full Development Cycle", text: "From audit and architecture design to UX/UI, development, deployment, and post-launch support." },
            { number: "02", title: "Transparent Timeline and Budget", text: "Two-week sprints with a demo after each one. Scope and cost are fixed in the contract — no hidden fees." },
            { number: "03", title: "Your Stack, Not Ours", text: "React, Next.js, Node.js, Python, Swift, Kotlin, Java, PHP, 1C. We integrate into your infrastructure instead of forcing a migration." },
            { number: "04", title: "Reliability and Security", text: "Automated tests, code review, monitoring, and secure development. On-premise work, compliant with 152-FZ and GDPR." },
        ],
        contacts: { label: "Contacts", chatLabel: "Discuss in chat" },
    },
};

export default function SeoBlock({ locale }: { locale: string }) {
    const copy = locale === "ru" ? seoCopy.ru : seoCopy.en;
    const localeFromHook = useLocale();
    const paragraphs = copy.intro.split("\n\n");

    return (
        <section className="py-10 md:py-14 max-md:py-9">
            <div className="lg:flex lg:items-start">

                {/* Контент — слева */}
                <div className="relative z-10 mt-10 px-4 lg:mt-0 lg:flex-1 lg:flex lg:justify-center lg:px-8 max-md:mt-0">
                    <div className="w-full max-w-3xl lg:-mr-36">
                        <h2 className="text-3xl font-bold tracking-tight text-white/95 max-md:text-2xl">
                            {copy.title}
                        </h2>
                        <div className="mt-3 max-w-3xl text-sm text-white/70 text-left max-md:mt-2">
                            {paragraphs.map((paragraph, index) => (
                                <p key={index} className="mb-4 last:mb-0">
                                    {paragraph}
                                </p>
                            ))}
                        </div>

                        {/* 8 направлений — та же сетка, что в блоке интеграции ИИ */}
                        <div className="mt-8 grid auto-rows-fr grid-cols-2 gap-2.5 sm:grid-cols-2 max-md:mt-6 sm:gap-3 md:gap-4">
                            {copy.cards.map((card) => (
                                <IntegrationCard key={card.abbr} {...card} />
                            ))}
                        </div>

                        {/* 4 преимущества */}
                        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 max-md:mt-6 max-md:gap-2.5">
                            {copy.benefits.map((benefit) => (
                                <BenefitCard key={benefit.number} {...benefit} />
                            ))}
                        </div>

                        {/* Контакты */}
                        <div className="mt-8 flex flex-wrap gap-3 max-md:mt-6">
                            <Link
                                href={getPathWithLocale("/contacts", localeFromHook)}
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={copy.contacts.label}
                            >
                                <ContactsIcon />
                            </Link>
                            <a
                                href={`mailto:${CONTACTS.email}`}
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={CONTACTS.email}
                            >
                                <MailIcon />
                            </a>
                            <a
                                href={`tel:${CONTACTS.phone}`}
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={CONTACTS.phoneDisplay}
                            >
                                <PhoneIcon />
                            </a>
                            <a
                                href={CONTACTS.whatsappUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={`WhatsApp ${CONTACTS.whatsappDisplay}`}
                            >
                                <WhatsAppIcon />
                            </a>
                            <a
                                href={CONTACTS.telegramUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={CONTACTS.telegramHandle}
                            >
                                <TelegramIcon />
                            </a>
                            <Link
                                href={getPathWithLocale("/chat", localeFromHook)}
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={copy.contacts.chatLabel}
                            >
                                <ChatIcon />
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Фото — справа, липкое: в блоке интеграции ИИ фото слева */}
                <Reveal
                    className="mt-12 lg:mt-0 lg:sticky lg:top-[80px] lg:self-start lg:shrink-0 lg:w-[448px] max-md:hidden"
                    scale={0.95}
                    duration={0.8}
                >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src="/images/seo-business-448.webp"
                        srcSet="/images/seo-business-448.webp 448w, /images/seo-business-714.webp 714w, /images/seo-business.webp 1173w"
                        sizes="(max-width: 768px) 100vw, 448px"
                        alt={copy.title}
                        loading="lazy"
                        width="448"
                        height="672"
                        className="w-full border border-white/10 object-cover aspect-[2/3] lg:translate-x-[calc(10vw_+_25px)]"
                    />
                </Reveal>
            </div>
        </section>
    );
}
